import type { CoreContext } from "./context";
import { isEmailValid, messageFromError } from "./format";
import type { IdentityFlows } from "./identity-flows";
import type { CoreState } from "./state";
import { isZeroTotal, limitProblemOf, sessionOf } from "./state";

/** How long typing pauses before a valid address is told to the engine. */
export const EMAIL_DEBOUNCE_MS = 500;

/**
 * The address the tickets go to: typed, settled into the chip, written to
 * the session in order, and asked about once (is this a Lissen buyer's?).
 * A free order completes from here too, since the address is all it needs.
 */
export function createEmailFlows(
  ctx: CoreContext,
  identity: IdentityFlows,
) {
  const { deps, config, set, sessionWrite, emit, refs } = ctx;
  const { buyer, wallet } = deps.engine;
  const { sessionId } = config;

  // The last submitted address must be the last write at the engine too.
  // Completion drains this queue before deciding whether one final write is
  // needed.
  const writeEmail = (address: string) => {
    if (!buyer) throw new Error("Checkout is temporarily unavailable.");
    const pendingWrite = refs.emailWrites.then(async () => {
      // Numbered as it is sent: a read made before it is older than it.
      const apply = sessionWrite();
      const updated = await buyer.update(sessionId, {
        customer: { email: address, name: null },
      });
      if (updated.data) refs.savedEmail = updated.data.buyer_email;
      return { updated, apply };
    });
    refs.emailWrites = pendingWrite.then(
      () => undefined,
      () => undefined,
    );
    return pendingWrite;
  };
  const isLatestEmailWrite = (revision: number) =>
    !refs.completing && revision === refs.emailRevision;

  /** A settled address: written to the session, announced, and — once per
   *  address — asked about, so a Lissen buyer's gets the code in place. */
  const commitEmail = async (view: CoreState, value: string) => {
    const session = sessionOf(view);
    if (!buyer || !session || !isEmailValid(value) || refs.completing) return;
    const address = value.trim();
    const revision = ++refs.emailRevision;
    let written;
    try {
      written = await writeEmail(address);
    } catch (error) {
      if (isLatestEmailWrite(revision))
        set({ notice: messageFromError(error, "We couldn't save your email.") });
      return;
    }
    const { updated } = written;
    if (!isLatestEmailWrite(revision)) return;
    if (!updated.data) {
      set({
        notice: messageFromError(updated.error, "We couldn't save your email."),
      });
      return;
    }
    written.apply({ type: "session_updated", session: updated.data });
    emit({ type: "contact", email: address });
    if (
      !isZeroTotal(view) &&
      config.identityAvailable &&
      wallet &&
      !view.attested &&
      refs.enrolmentFor !== address
    ) {
      refs.enrolmentFor = address;
      try {
        const { enrolled } = await wallet.enrolment(session.id, {
          email: address,
        });
        if (enrolled)
          await identity.startCode(
            view,
            { email: address },
            { kind: "wallet" },
          );
      } catch {
        // Not knowing is the same as no: the guest path is unchanged.
      }
    }
  };

  const clearDebounce = () => {
    if (refs.debounce !== null) deps.clock.clearTimeout(refs.debounce);
    refs.debounce = null;
  };

  /** A keystroke: the field follows, and a valid address is committed after
   *  a pause in typing. */
  const typeEmail = (view: CoreState, next: string) => {
    set({ email: { value: next, chip: false, source: "typed" } });
    clearDebounce();
    if (isEmailValid(next))
      refs.debounce = deps.clock.setTimeout(
        () => void commitEmail(view, next),
        EMAIL_DEBOUNCE_MS,
      );
  };

  /** Leaving the field settles a valid address into the chip. */
  const settleEmail = (view: CoreState) => {
    if (isEmailValid(view.email.value))
      set((s) => ({ email: { ...s.email, chip: true } }));
  };

  /** Enter: settle and commit now. */
  const submitEmail = (view: CoreState) => {
    if (!isEmailValid(view.email.value)) return;
    set((s) => ({ email: { ...s.email, chip: true } }));
    clearDebounce();
    void commitEmail(view, view.email.value);
  };

  /** A free order: the latest address written, then the tickets issued. */
  const completeFree = async (view: CoreState) => {
    const session = sessionOf(view);
    if (!buyer || !session || view.busy !== null || refs.completing) return;
    if (config.hostIdentity && !view.attested) return;
    // Outside the event's limits the engine would refuse: say what to
    // choose instead (a host's free order completes without a press).
    const outsideLimits = limitProblemOf(view);
    if (outsideLimits) {
      set({ notice: outsideLimits });
      return;
    }
    refs.completing = true;
    clearDebounce();
    set({ busy: "complete", notice: null });
    try {
      await refs.emailWrites;
      const address = view.email.value.trim();
      if (!view.attested && (refs.savedEmail ?? session.buyer_email) !== address) {
        const { updated, apply } = await writeEmail(address);
        if (!updated.data) throw updated.error;
        apply({ type: "session_updated", session: updated.data });
      }
      const completed = sessionWrite();
      const result = await buyer.complete(session.id);
      if (!result.data) throw result.error;
      completed({ type: "session_updated", session: result.data });
    } catch (error) {
      set({
        notice: messageFromError(
          error,
          "We couldn't issue your tickets. Try again.",
        ),
      });
    } finally {
      refs.completing = false;
      set({ busy: null });
    }
  };

  return {
    commitEmail,
    typeEmail,
    settleEmail,
    submitEmail,
    completeFree,
    clearDebounce,
  };
}

export type EmailFlows = ReturnType<typeof createEmailFlows>;
